import { groupBy } from 'lodash';
import { baseConfig } from '@com/core/common/baseConfig';
import { TVariant, TOffer, TProduct, TFbtOffer } from '@com/core/common/types/catalog';

type TProductProps = {
    sku: string;
    url?: string;
    offer: {
        id: string;
    };
};

export const getProductUrl = (
    product: TProductProps | TProduct | TFbtOffer,
    isCompleteUrl: boolean,
    urlParamString?: string,
): string => {
    if (!product) {
        return '/';
    }
    const productStr = product.url || 'product';
    const sku = product.sku || '';
    const [skuString] = sku.split('-');
    const offerCode = product?.offer?.id || product?.offer_code || product?.offerCode;
    const offerStr = offerCode ? `?o=${offerCode}` : '';
    let productUrl = `${productStr}/${skuString}/p${offerStr}`;

    if (urlParamString) {
        if (offerStr) {
            productUrl = `${productUrl}&${urlParamString}`;
        } else {
            productUrl = `${productUrl}?${urlParamString}`;
        }
    }

    return isCompleteUrl ? productUrl : `${baseConfig.urls.base.replace(/\/$/, '')}/${productUrl}`;
};

export const getOfferFromVariants = (variants: TVariant[] = [], offerCode = ''): TOffer | null => {
    let offer = null;

    // Get the variants with offers
    const variantsWithOffers = variants.filter(v => v.offers && v.offers.length > 0);

    if (variantsWithOffers.length) {
        // If no offer code, get first offer of first variant
        if (!offerCode) {
            // Get first variant with offers
            [offer] = variantsWithOffers[0].offers;
        } else {
            // Get the first variant with matching offer code
            offer =
                variantsWithOffers
                    .map(variantsWithOffer => {
                        return variantsWithOffer.offers.find(variantOffer => {
                            return variantOffer.offer_code === offerCode;
                        });
                    })
                    .filter(x => x)[0] || null;
        }
    }

    return offer;
};

export const getOffersForVariant = (variants: TVariant[] = [], sku: string): TOffer[] => {
    const variant: TVariant | undefined = variants.find(v => v.sku === sku);

    if (!variant) {
        const variantWithOffers = variants.find(v => v.offers && v.offers.length > 0);
        if (variantWithOffers && Array.isArray(variantWithOffers.offers)) {
            return variantWithOffers.offers;
        }
        return [];
    }

    return variant.offers;
};

export const getVariantSizingWithOffers = (
    variants: TVariant[],
): { name: string; sku: string; disabled: boolean }[] | null => {
    if (variants) {
        const variantsArr = variants.map((variant: TVariant) => ({
            name: variant.variant,
            sku: variant.sku,
            disabled: !(variant.offers && variant.offers.length > 0),
        }));
        const variantsGroup = groupBy(variantsArr, variant => (variant.disabled ? 'notAvailable' : 'available'));

        return [...(variantsGroup.available || []), ...(variantsGroup.notAvailable || [])];
    }
    return null;
};

export const getOfferCodeFromUrl = (): string | null => {
    const isBrowser: boolean = typeof window !== 'undefined';
    if (!isBrowser) {
        return '';
    }

    const urlParams = new URLSearchParams(window.location.search);
    const offerCode = urlParams.get('o');
    return offerCode;
};

export const hasProductIdentifiers = (offer: TOffer): boolean => {
    return (
        Object.prototype.hasOwnProperty.call(offer, 'flags') &&
        Array.isArray(offer.flags) &&
        (offer.flags.includes('fbn') || offer.flags.includes('marketplace') || offer.flags.includes('preorder'))
    );
};

export const getPrioritizedProductIdentifierType = (offer: TOffer): string | null => {
    if (Object.prototype.hasOwnProperty.call(offer, 'flags') && Array.isArray(offer.flags)) {
        if (offer.flags.includes('preorder')) {
            return 'preorder';
        }
        if (offer.flags.includes('fbn')) {
            return 'fbn';
        }
        if (offer.flags.includes('marketplace')) {
            return 'marketplace';
        }
    }
    return null;
};

export const getShippingEstimatorType = (flags: string[]): string => {
    if (!Array.isArray(flags)) {
        return '';
    }
    switch (true) {
        case flags.includes('preorder'):
            return 'preorder';
        case flags.includes('fbn'):
            return 'fbn';
        case flags.includes('marketplace'):
            return 'marketplace';
        default:
            return '';
    }
};

export const getOtherOffers = (offers: TOffer[], selectedOfferCode?: string): TOffer[] => {
    if (!selectedOfferCode) return offers;
    return offers.filter(({ offer_code }) => offer_code !== selectedOfferCode);
};

export const getCheapestPrice = (offers: TOffer[], exceptOfferCode?: string): number => {
    let allOffers = offers;
    if (exceptOfferCode) {
        allOffers = getOtherOffers(offers, exceptOfferCode);
    }
    return allOffers.reduce((min, offer) => {
        if (offer.sale_price) {
            return offer.sale_price < min ? offer.sale_price : min;
        }
        return offer.price < min ? offer.price : min;
    }, allOffers[0].sale_price || allOffers[0].price);
};

export const getProductSlugFromTitle = (product: TProduct): string =>
    product.product_title
        .trim()
        .toLowerCase()
        .replace(/[^0-9a-zA-Z]+/g, '-');
